import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  Clock,
  Sparkles,
  Zap,
  ShieldCheck,
  Handshake,
  MapPin,
} from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import PlanShowcase from "@/components/site/PlanShowcase";
import ServiceMap from "@/components/site/ServiceMap";
import Faq from "@/components/site/Faq";
import HeroFinishes from "@/components/site/HeroFinishes";
import QuickRequestModal from "@/components/forms/QuickRequestModal";
import { useAuth } from "@/context/AuthContext";

const RLAH_TAG = "RLAH agent request";

const PERKS = [
  {
    icon: Zap,
    title: "Priority queue",
    desc: "RLAH agents jump the line — priority queue on standard 2D & 3D orders.",
    tint: "bg-amber-100 text-amber-700",
  },
  {
    icon: BadgeCheck,
    title: "Agent pricing",
    desc: "RLAH agents get a flat 15% off regular pricing on every order — 2D, 3D, and renew.",
    tint: "bg-teal-100 text-teal-700",
  },
  {
    icon: ShieldCheck,
    title: "MLS-ready delivery",
    desc: "Every plan drafted to ANSI/REBAC standards. Editable PDF, PNG and DXF included.",
    tint: "bg-rose-100 text-rose-700",
  },
  {
    icon: Handshake,
    title: "Direct dispatch",
    desc: "We measure on-site within one business day of your request. No middlemen.",
    tint: "bg-sky-100 text-sky-700",
  },
];

const SERVICES = [
  {
    key: "2d",
    planType: "2D Floor Plan",
    title: "2D Floor Plans",
    accent: "15% off regular",
    desc: "MLS-ready B&W or colorized with precise dimensions and total sqft.",
    testId: "rlah-service-2d",
    tint: "bg-teal-50",
    chipTint: "bg-teal-100 text-teal-800",
  },
  {
    key: "3d",
    planType: "3D Floor Plan",
    title: "3D Floor Plans",
    accent: "15% off regular",
    desc: "Photo-realistic renderings with furniture, textures and finishes.",
    testId: "rlah-service-3d",
    tint: "bg-amber-50",
    chipTint: "bg-amber-100 text-amber-800",
  },
  {
    key: "renew",
    planType: "Renew Existing Floor Plan",
    title: "Renew Old Plans",
    accent: "15% off regular",
    desc: "Refresh your existing plan — clean linework, updated dimensions, modern formats.",
    testId: "rlah-service-renew",
    tint: "bg-rose-50",
    chipTint: "bg-rose-100 text-rose-800",
  },
];

export default function RLAH() {
  const { user, authHeader } = useAuth();
  const [selectedService, setSelectedService] = useState(null);

  return (
    <main
      className="min-h-screen bg-[#FAF7F2] text-stone-900"
      data-testid="rlah-page-root"
    >
      <Nav />

      {/* Hero */}
      <section
        className="relative pt-24 pb-16 lg:pt-32 lg:pb-20 overflow-hidden text-white"
        data-testid="rlah-hero"
      >
        {/* DC skyline background */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1617581629397-a72507c3de9e?auto=format&fit=crop&w=2400&q=80')",
          }}
          aria-hidden="true"
        />
        {/* Dark gradient overlay for legibility */}
        <div
          className="absolute inset-0 bg-gradient-to-br from-stone-900/85 via-stone-900/70 to-teal-900/85"
          aria-hidden="true"
        />

        <div className="relative mx-auto max-w-7xl px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-5 fade-up">
            <span
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.18em] text-white mb-5"
              data-testid="rlah-hero-badge"
            >
              <BadgeCheck className="h-3.5 w-3.5 text-amber-300" />
              RLAH Agent Portal
            </span>

            <h1
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.04] tracking-tight text-white"
              data-testid="rlah-hero-headline"
            >
              Built for <br />
              <span className="italic font-medium">RLAH agents</span>
            </h1>

            <p className="mt-2 text-amber-100 text-sm font-semibold italic">
              Real Estate · Residential · Commercial
            </p>

            <p
              className="mt-5 max-w-xl text-sm text-stone-100/90 leading-relaxed"
              data-testid="rlah-hero-subheadline"
            >
              Preferred pricing, priority turnaround, and the same
              on-site accuracy your listings deserve — for the RLAH team only.
              We measure, draft, and deliver so you can focus on selling.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() =>
                  setSelectedService({ planType: "2D Floor Plan", tag: "RLAH" })
                }
                data-testid="rlah-hero-cta-primary"
                className="group inline-flex items-center gap-2 bg-white text-stone-900 px-6 py-3 rounded-full text-sm font-semibold hover:bg-amber-200 transition-colors shadow-lg"
              >
                Request a Plan
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <Link
                to="/pricing"
                data-testid="rlah-hero-cta-pricing"
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/25 px-6 py-3 rounded-full text-sm font-semibold text-white hover:bg-white/20 transition-colors"
              >
                See Agent Pricing
              </Link>
              <a
                href="#rlah-perks"
                data-testid="rlah-hero-cta-secondary"
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/25 px-6 py-3 rounded-full text-sm font-semibold text-white hover:bg-white/20 transition-colors"
              >
                See Agent Perks
              </a>
              <a
                href="#service-area"
                data-testid="rlah-hero-cta-service-area"
                className="inline-flex items-center gap-2 text-sm font-semibold text-amber-200 hover:text-white px-2 py-3 border-b border-transparent hover:border-amber-200 transition-colors"
              >
                <MapPin className="h-4 w-4" />
                See Service Area
              </a>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-5 max-w-md">
              <HeroStat
                label="Turnaround"
                value="Priority"
                testId="rlah-stat-turnaround"
              />
              <HeroStat
                label="Agent pricing"
                value="15% off regular"
                testId="rlah-stat-pricing"
              />
              <HeroStat
                label="Location"
                value="DC Metropolitan Area"
                testId="rlah-stat-location"
              />
            </div>
          </div>

          <div className="lg:col-span-7 relative" data-testid="rlah-hero-visual">
            <div className="rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-white/20">
              <HeroFinishes />
            </div>
          </div>
        </div>
      </section>

      {/* Perks */}
      <section
        id="rlah-perks"
        className="py-20 lg:py-24 px-6 lg:px-12 bg-[#FAF7F2]"
        data-testid="rlah-perks-section"
      >
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-14">
            <div className="lg:col-span-7">
              <span className="inline-block bg-white border border-stone-200 px-3 py-1.5 rounded-full text-xs font-semibold text-stone-700 mb-5">
                01 — Why RLAH agents choose us
              </span>
              <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">
                Four perks, <br />
                <span className="italic font-medium text-teal-700">no fine print.</span>
              </h2>
            </div>
            <div className="lg:col-span-5 lg:pl-8">
              <p className="text-stone-600 text-base leading-relaxed">
                We work directly with RLAH agents across the DC Metro. Everything on
                this page is available <strong>exclusively</strong> when you request
                through <code className="text-xs font-semibold bg-stone-100 px-1.5 py-0.5 rounded">dmvfp.com/rlah</code>.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {PERKS.map((p, idx) => (
              <PerkCard key={p.title} index={idx} {...p} />
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section
        className="py-20 lg:py-24 px-6 lg:px-12 bg-white border-y border-stone-200"
        data-testid="rlah-services-section"
      >
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
            <div className="lg:col-span-7">
              <span className="inline-block bg-[#FAF7F2] border border-stone-200 px-3 py-1.5 rounded-full text-xs font-semibold text-stone-700 mb-5">
                02 — RLAH pricing
              </span>
              <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">
                Preferred agent rates.<br />
                <span className="italic font-medium text-teal-700">Tap a service to request.</span>
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, idx) => (
              <RLAHServiceCard
                key={s.key}
                index={idx}
                {...s}
                onClick={() => setSelectedService(s)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio snapshot */}
      <PlanShowcase />

      {/* Service area map */}
      <ServiceMap />

      {/* Fees & FAQ */}
      <Faq />

      {/* Contact strip */}
      <section
        className="py-20 lg:py-24 px-6 lg:px-12 bg-[#FAF7F2]"
        data-testid="rlah-contact-strip"
      >
        <div className="mx-auto max-w-4xl bg-teal-700 text-white rounded-[2rem] p-10 lg:p-14 text-center relative overflow-hidden">
          <div className="absolute -right-16 -bottom-16 w-72 h-72 rounded-full bg-amber-300/30 blur-3xl pointer-events-none" />
          <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1.5 rounded-full text-xs font-semibold mb-5">
            <Sparkles className="h-3.5 w-3.5 text-amber-300" />
            RLAH — go faster
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
            One listing away.
          </h2>
          <p className="mt-4 text-teal-50/90 max-w-xl mx-auto">
            Send us the address — we&apos;ll get on the schedule fast and your
            MLS-ready plan lands in your inbox as soon as it&apos;s ready.
          </p>
          <div className="mt-8 inline-flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() =>
                setSelectedService({ planType: "2D Floor Plan", tag: "RLAH" })
              }
              data-testid="rlah-strip-cta"
              className="group inline-flex items-center gap-2 bg-white text-stone-900 px-6 py-3 rounded-full text-sm font-semibold hover:bg-amber-100 transition-colors shadow-sm"
            >
              Request now
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <a
              href="tel:+15713318224"
              data-testid="rlah-strip-call"
              className="inline-flex items-center gap-2 bg-white/15 backdrop-blur border border-white/25 text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-white/25 transition-colors"
            >
              Call (571) 331-8224
            </a>
          </div>
          <p className="mt-8 text-xs text-teal-100/80">
            Not with RLAH?{" "}
            <Link
              to="/"
              className="underline font-semibold hover:text-white"
              data-testid="rlah-strip-back-home"
            >
              Head back to the main site →
            </Link>
          </p>
        </div>
      </section>

      <Footer />

      <QuickRequestModal
        open={!!selectedService}
        onClose={() => setSelectedService(null)}
        prefill={{
          name: user?.name,
          email: user?.email,
          planType: selectedService?.planType,
          messagePrefix: RLAH_TAG,
        }}
        authHeader={authHeader}
      />
    </main>
  );
}

function Stat({ label, value, tint, testId }) {
  return (
    <div data-testid={testId} className="space-y-1">
      <span
        className={`inline-block ${tint} text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full`}
      >
        {label}
      </span>
      <div className="font-display text-xl font-semibold text-stone-900">
        {value}
      </div>
    </div>
  );
}

function HeroStat({ label, value, testId }) {
  return (
    <div data-testid={testId} className="space-y-1">
      <span className="inline-block bg-white/15 backdrop-blur border border-white/20 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">
        {label}
      </span>
      <div className="font-display text-lg font-semibold text-white leading-tight">
        {value}
      </div>
    </div>
  );
}

function PerkCard({ icon: Icon, title, desc, tint, index }) {
  return (
    <div
      className="bg-white border border-stone-200 rounded-[2rem] p-6 lg:p-7 flex items-start gap-4 hover:-translate-y-0.5 hover:shadow-md transition-all"
      data-testid={`rlah-perk-${index}`}
    >
      <div
        className={`h-12 w-12 rounded-2xl ${tint} flex items-center justify-center flex-shrink-0`}
      >
        <Icon className="h-5 w-5" strokeWidth={1.75} />
      </div>
      <div>
        <h3 className="font-display text-lg font-semibold">{title}</h3>
        <p className="text-stone-600 text-sm mt-1.5 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function RLAHServiceCard({
  title,
  desc,
  accent,
  index,
  tint,
  chipTint,
  onClick,
  testId,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`group relative text-left ${tint} border border-stone-200 rounded-[2rem] p-8 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2 focus:ring-offset-white`}
    >
      <div className="flex items-start justify-between mb-6">
        <Clock className="h-6 w-6 text-stone-700" strokeWidth={1.75} />
        <div className="h-9 w-9 rounded-full bg-white border border-stone-200 flex items-center justify-center group-hover:bg-stone-900 group-hover:text-white transition-colors">
          <ArrowRight className="h-4 w-4" />
        </div>
      </div>
      <span
        className={`inline-block ${chipTint} px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider mb-4`}
      >
        0{index + 1} · {accent}
      </span>
      <h3 className="font-display text-2xl font-semibold text-stone-900 leading-tight">
        {title}
      </h3>
      <p className="mt-3 text-stone-600 text-sm leading-relaxed">{desc}</p>
      <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-stone-700 group-hover:text-teal-700 transition-colors">
        Request this →
      </span>
    </button>
  );
}
